import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import AppButton from '../../../../components/designe/app-button/AppButton';
import ConfirmDialog from '../../../../components/reusable/confirm-dialog/ConfirmDialog';
import AppDialog from '../../../../components/reusable/app-dialog/AppDialog';
import FlexibleColumnsTable from '../../../../components/reusable/Table/FlexibleColumnsTable';
import { useAccess } from '../../../../hooks/useAccess';
import { useGetLanguages } from '../../../../services/redux/api/languagesAPI';
import {
  useCreateNotificationTranslationMutation,
  useDeleteNotificationTranslationMutation,
  useGetNotificationTemplateByIdQuery,
  useGetNotificationTranslationsQuery,
  usePatchNotificationTemplateMutation,
  usePatchNotificationTranslationMutation,
} from '../../../../services/redux/api/notificationsAdminApi';
import {
  NOTIFICATION_DISPLAY_DOMAINS,
  SYSTEM_MAINTENANCE_PLANNED_TEMPLATE_CODE,
  type INotificationTemplateTranslation,
  type NotificationDisplayDomain,
  type NotificationPreferenceGroup,
  type NotificationTemplateCategory,
} from '../../../../types/interfaces/notification-admin';
import {
  isCategoryPreferenceConsistent,
  preferenceGroupsForCategory,
} from '../../../../utils/notification-admin-rules';
import {
  notificationCategoryLabel,
  notificationDisplayDomainLabel,
  notificationPreferenceGroupLabel,
} from '../../../../utils/notification-template-labels';
import theme from '../../../../styles/theme';
import { notificationsBasePath } from '../notificationsBasePath';
import { useNotificationsTableStyles } from '../NotificationsTable.styles';
import { NotificationPreviewModal } from './NotificationPreviewModal';
import { useNotificationTemplateDetailTranslationsTable } from './useNotificationTemplateDetailTranslationsTable';

export function NotificationTemplateDetail() {
  const { classes } = useNotificationsTableStyles(theme);
  const { t } = useTranslation();
  const { templateId = '' } = useParams();
  const navigate = useNavigate();
  const { access } = useAccess();
  const canMutate =
    (access?.notifications?.canCreate || access?.notifications?.canEdit) ??
    false;
  const canDelete = access?.notifications?.canDelete ?? false;

  const { data: template, isLoading: loadingT } =
    useGetNotificationTemplateByIdQuery(templateId, { skip: !templateId });
  const { data: translations = [], isLoading: loadingTr } =
    useGetNotificationTranslationsQuery(templateId, { skip: !templateId });
  const { data: languages = [] } = useGetLanguages();

  const [patchTemplate, { isLoading: savingT }] =
    usePatchNotificationTemplateMutation();
  const [createTr, { isLoading: creatingTr }] =
    useCreateNotificationTranslationMutation();
  const [patchTr, { isLoading: savingTr }] =
    usePatchNotificationTranslationMutation();
  const [deleteTr] = useDeleteNotificationTranslationMutation();

  const [metaEditOpen, setMetaEditOpen] = useState(false);
  const [code, setCode] = useState('');
  const [category, setCategory] =
    useState<NotificationTemplateCategory>('marketing');
  const [preferenceGroup, setPreferenceGroup] =
    useState<NotificationPreferenceGroup>('marketing');
  const [displayDomain, setDisplayDomain] =
    useState<NotificationDisplayDomain>('generic');
  const [active, setActive] = useState(true);

  const [translationEditorOpen, setTranslationEditorOpen] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState<
    INotificationTemplateTranslation | 'new' | null
  >(null);
  const [trLang, setTrLang] = useState('');
  const [trTitle, setTrTitle] = useState('');
  const [trBody, setTrBody] = useState('');
  const [trImage, setTrImage] = useState('');
  const [deleteTrId, setDeleteTrId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const openMetaEdit = useCallback(() => {
    if (!template) return;
    setCode(template.code);
    setCategory(template.category);
    setPreferenceGroup(template.preferenceGroup);
    setDisplayDomain(template.displayDomain);
    setActive(template.active);
    setMetaEditOpen(true);
  }, [template]);

  const onCategoryChange = (c: NotificationTemplateCategory) => {
    setCategory(c);
    const allowed = preferenceGroupsForCategory(c);
    if (c === 'marketing') {
      setPreferenceGroup('marketing');
    } else if (!allowed.includes(preferenceGroup)) {
      setPreferenceGroup(allowed[0]);
    }
  };

  const saveTemplate = async () => {
    const resolvedPreference =
      category === 'marketing' ? 'marketing' : preferenceGroup;
    if (
      !templateId ||
      !isCategoryPreferenceConsistent(category, resolvedPreference)
    )
      return;
    await patchTemplate({
      id: templateId,
      body: {
        code: code.trim(),
        category,
        preferenceGroup: resolvedPreference,
        displayDomain,
        active,
      },
    }).unwrap();
    setMetaEditOpen(false);
  };

  const langById = useMemo(() => {
    const m = new Map<string, string>();
    languages.forEach((l) => {
      if (l.id) m.set(l.id, l.title || l.key || l.id);
    });
    return m;
  }, [languages]);

  const langLabel = useCallback(
    (languageId: string) => langById.get(languageId) ?? languageId,
    [langById],
  );

  const openEditTranslation = useCallback(
    (row: INotificationTemplateTranslation) => {
      setEditingTranslation(row);
      setTrLang(row.languageId);
      setTrTitle(row.title);
      setTrBody(row.body);
      setTrImage(row.imageUrl ?? '');
      setTranslationEditorOpen(true);
    },
    [],
  );

  const setDeleteTranslation = useCallback(
    (row: INotificationTemplateTranslation) => {
      setDeleteTrId(row.id);
    },
    [],
  );

  const {
    sortedArray: translationRows,
    columnsList: translationColumnsList,
    order: translationOrder,
    orderBy: translationOrderBy,
    cellReducers: translationCellReducers,
    onSort: translationOnSort,
    onSortColumns: translationOnSortColumns,
  } = useNotificationTemplateDetailTranslationsTable(
    translations,
    t,
    classes,
    canMutate,
    canDelete,
    langLabel,
    openEditTranslation,
    setDeleteTranslation,
  );

  const usedLanguageIds = useMemo(
    () => new Set(translations.map((x) => x.languageId)),
    [translations],
  );

  const openNewTranslation = () => {
    setEditingTranslation('new');
    setTrLang('');
    setTrTitle('');
    setTrBody('');
    setTrImage('');
    setTranslationEditorOpen(true);
  };

  const saveTranslationFromEditor = async () => {
    if (!templateId || !trLang || !trTitle.trim()) return;
    if (editingTranslation === 'new') {
      await createTr({
        templateId,
        body: {
          languageId: trLang,
          title: trTitle,
          body: trBody,
          imageUrl: trImage.trim() ? trImage.trim() : null,
        },
      }).unwrap();
    } else if (editingTranslation) {
      await patchTr({
        templateId,
        translationId: editingTranslation.id,
        body: {
          title: trTitle,
          body: trBody,
          imageUrl: trImage.trim() ? trImage.trim() : null,
        },
      }).unwrap();
    }
    setTranslationEditorOpen(false);
    setEditingTranslation(null);
  };

  const confirmDeleteTr = async () => {
    if (!templateId || !deleteTrId) return;
    await deleteTr({ templateId, translationId: deleteTrId }).unwrap();
    setDeleteTrId(null);
  };

  if (!templateId) {
    return null;
  }

  if (loadingT || !template) {
    return (
      <div className={classes.wrapper}>
        <Typography>{t('adminDashboard.notifications.loading')}</Typography>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <AppButton
        variant="outlined"
        onClick={() => navigate(`${notificationsBasePath}/templates`)}
        sx={{ mb: 2 }}>
        {t('adminDashboard.notifications.backToList')}
      </AppButton>

      <Typography variant="h6" gutterBottom>
        {template.code}
      </Typography>

      {template.category === 'transactional' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('adminDashboard.notifications.templates.transactionalHint')}
        </Alert>
      )}
      {template.code === SYSTEM_MAINTENANCE_PLANNED_TEMPLATE_CODE && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('adminDashboard.notifications.templates.maintenanceTemplateHint')}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1 }}>
        <Typography variant="subtitle1">
          {t('adminDashboard.notifications.templates.metaSection')}
        </Typography>
        {canMutate && (
          <AppButton onClick={openMetaEdit}>{t('common.edit')}</AppButton>
        )}
      </Box>
      <div className={classes.metaFields}>
        <div className={classes.metaField}>
          <Typography variant="body2" className={classes.metaLabel}>
            {t('adminDashboard.notifications.templates.code')}
          </Typography>
          <Typography variant="body2">{template.code}</Typography>
        </div>
        <div className={classes.metaField}>
          <Typography variant="body2" className={classes.metaLabel}>
            {t('adminDashboard.notifications.templates.category')}
          </Typography>
          <Typography variant="body2">
            {notificationCategoryLabel(t, template.category)}
          </Typography>
        </div>
        <div className={classes.metaField}>
          <Typography variant="body2" className={classes.metaLabel}>
            {t('adminDashboard.notifications.templates.preferenceGroup')}
          </Typography>
          <Typography variant="body2">
            {notificationPreferenceGroupLabel(t, template.preferenceGroup)}
          </Typography>
        </div>
        <div className={classes.metaField}>
          <Typography variant="body2" className={classes.metaLabel}>
            {t('adminDashboard.notifications.templates.displayDomain')}
          </Typography>
          <Typography variant="body2">
            {notificationDisplayDomainLabel(t, template.displayDomain)}
          </Typography>
        </div>
        <div className={classes.metaField}>
          <Typography variant="body2" className={classes.metaLabel}>
            {t('adminDashboard.notifications.templates.active')}
          </Typography>
          <Typography variant="body2">
            {template.active ? t('common.active') : t('common.notActive')}
          </Typography>
        </div>
        <div className={classes.metaField}>
          <Typography variant="body2" className={classes.metaLabel}>
            {t('adminDashboard.notifications.templates.updatedAt')}
          </Typography>
          <Typography variant="body2">
            {new Date(template.updatedAt).toLocaleString()}
          </Typography>
        </div>
      </div>
      <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 3 }}>
        <AppButton variant="outlined" onClick={() => setPreviewOpen(true)}>
          {t('adminDashboard.notifications.preview.open')}
        </AppButton>
      </Box>

      <Typography variant="subtitle1" gutterBottom>
        {t('adminDashboard.notifications.translations.sectionTitle')}
      </Typography>
      {canMutate && (
        <AppButton sx={{ mb: 1 }} onClick={openNewTranslation}>
          {t('adminDashboard.notifications.translations.add')}
        </AppButton>
      )}

      {loadingTr ? (
        <Typography>{t('adminDashboard.notifications.loading')}</Typography>
      ) : (
        <FlexibleColumnsTable
          data={translationRows}
          columnsList={translationColumnsList}
          order={translationOrder}
          orderBy={translationOrderBy}
          onSort={translationOnSort}
          classes={classes}
          onSortColumns={translationOnSortColumns}
          cellReducers={translationCellReducers}
          onSelectAllRows={() => {}}
          stickyHeader={true}
        />
      )}

      <AppDialog
        open={metaEditOpen}
        onClose={() => setMetaEditOpen(false)}
        title={t('adminDashboard.notifications.templates.editMetaTitle')}>
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ minWidth: 320 }}>
          <TextField
            label={t('adminDashboard.notifications.templates.code')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="td-cat">
              {t('adminDashboard.notifications.templates.category')}
            </InputLabel>
            <Select
              labelId="td-cat"
              label={t('adminDashboard.notifications.templates.category')}
              value={category}
              onChange={(e) =>
                onCategoryChange(e.target.value as NotificationTemplateCategory)
              }>
              <MenuItem value="marketing">
                {notificationCategoryLabel(t, 'marketing')}
              </MenuItem>
              <MenuItem value="transactional">
                {notificationCategoryLabel(t, 'transactional')}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="td-pref">
              {t('adminDashboard.notifications.templates.preferenceGroup')}
            </InputLabel>
            <Select
              labelId="td-pref"
              label={t(
                'adminDashboard.notifications.templates.preferenceGroup',
              )}
              value={preferenceGroup}
              onChange={(e) =>
                setPreferenceGroup(
                  e.target.value as NotificationPreferenceGroup,
                )
              }>
              {preferenceGroupsForCategory(category).map((p) => (
                <MenuItem key={p} value={p}>
                  {notificationPreferenceGroupLabel(t, p)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="td-domain">
              {t('adminDashboard.notifications.templates.displayDomain')}
            </InputLabel>
            <Select
              labelId="td-domain"
              label={t('adminDashboard.notifications.templates.displayDomain')}
              value={displayDomain}
              onChange={(e) =>
                setDisplayDomain(e.target.value as NotificationDisplayDomain)
              }>
              {NOTIFICATION_DISPLAY_DOMAINS.map((d) => (
                <MenuItem key={d} value={d}>
                  {notificationDisplayDomainLabel(t, d)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box display="flex" alignItems="center" gap={1}>
            <Checkbox
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            <Typography variant="body2">
              {t('adminDashboard.notifications.templates.active')}
            </Typography>
          </Box>
          <AppButton
            onClick={saveTemplate}
            disabled={
              savingT ||
              !code.trim() ||
              !isCategoryPreferenceConsistent(
                category,
                category === 'marketing' ? 'marketing' : preferenceGroup,
              )
            }>
            {t('common.save')}
          </AppButton>
        </Box>
      </AppDialog>

      <AppDialog
        open={translationEditorOpen}
        onClose={() => {
          setTranslationEditorOpen(false);
          setEditingTranslation(null);
        }}
        title={
          editingTranslation === 'new'
            ? t('adminDashboard.notifications.translations.add')
            : t('adminDashboard.notifications.translations.edit')
        }>
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ minWidth: 360 }}>
          <FormControl fullWidth>
            <InputLabel id="trl">{t('common.language')}</InputLabel>
            <Select
              labelId="trl"
              label={t('common.language')}
              value={trLang}
              disabled={editingTranslation !== 'new'}
              onChange={(e) => setTrLang(e.target.value as string)}>
              {languages
                .filter((l) => l.id)
                .filter(
                  (l) =>
                    editingTranslation !== 'new' ||
                    !usedLanguageIds.has(l.id as string),
                )
                .map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.title || l.key}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            label={t('common.title')}
            value={trTitle}
            onChange={(e) => setTrTitle(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('common.description')}
            value={trBody}
            onChange={(e) => setTrBody(e.target.value)}
            fullWidth
            multiline
            minRows={4}
          />
          <TextField
            label={t('adminDashboard.notifications.translations.imageUrl')}
            value={trImage}
            onChange={(e) => setTrImage(e.target.value)}
            fullWidth
          />
          <AppButton
            onClick={saveTranslationFromEditor}
            disabled={creatingTr || savingTr || !trLang || !trTitle.trim()}>
            {t('common.save')}
          </AppButton>
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={!!deleteTrId}
        onClose={() => setDeleteTrId(null)}
        title={t('common.delete')}
        onSubmit={confirmDeleteTr}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>

      <NotificationPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        templateCode={template.code}
      />
    </div>
  );
}
